/**
 * The console, as a single self-contained page.
 *
 * ⛔ Tokens, panels, pills and button variants are lifted from the exemplars named in
 * docs/spec/ui.md — verdict's `src/web/page.ts` and instance-dashboard's `kit.tsx`/`ui.tsx`.
 * Approximating the palette produces a near-miss, which reads worse than not trying.
 *
 * ⛔ No CDN, no external font, no remote asset. It must work with no network beyond the APIs it calls.
 */

export const PAGE = /* html */ `<!doctype html>
<html lang="en" class="">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>sibyl</title>
<script>
// ⭐ THE THEME SYSTEM IS INSTANCE-DASHBOARD'S, KEY FOR KEY (its app/layout.tsx bootstrap and
// app/components/nav.tsx appearance menu). Runs before paint so there is no flash of the wrong
// appearance. Reads the persisted prefs and applies them to <html>:
//   • sibyl-theme   (dark|light|system, default system honouring prefers-color-scheme)
//   • sibyl-accent  (indigo default → no attribute; else 9 palette hues)
//   • sibyl-bg      ("off" hides the background image → solid theme)
//   • sibyl-glass   (0.35–1 panel translucency → inline --glass-alpha; wins over html.no-bg)
//   • sibyl-blur    (0–24px backdrop blur → inline --glass-blur)
//   • sibyl-ink / sibyl-surface / sibyl-bgcolor (freeform colour overrides → inline --color-ink /
//                    --surface-solid / --color-bg; only set when somebody picked one)
(function(){try{
  var d=document.documentElement;
  var t=localStorage.getItem('sibyl-theme')||'system';
  var light=t==='light'||(t==='system'&&!matchMedia('(prefers-color-scheme: dark)').matches);
  if(light)d.classList.add('light');
  var a=localStorage.getItem('sibyl-accent'); if(a&&a!=='indigo')d.setAttribute('data-accent',a);
  if(localStorage.getItem('sibyl-bg')==='off')d.classList.add('no-bg');
  var g=parseFloat(localStorage.getItem('sibyl-glass')); if(g>=0.35&&g<=1)d.style.setProperty('--glass-alpha',String(g));
  var b=parseFloat(localStorage.getItem('sibyl-blur')); if(b>=0&&b<=24)d.style.setProperty('--glass-blur',b+'px');
  var ink=localStorage.getItem('sibyl-ink'); if(ink)d.style.setProperty('--color-ink',ink);
  var sf=localStorage.getItem('sibyl-surface'); if(sf)d.style.setProperty('--surface-solid',sf);
  var bgc=localStorage.getItem('sibyl-bgcolor'); if(bgc)d.style.setProperty('--color-bg',bgc);
}catch(e){}})();
</script>
<style>
:root{
  --color-bg: oklch(0.16 0.012 264);
  --surface-solid: oklch(0.27 0.016 264);
  --color-accent: oklch(0.72 0.13 250);
  --surface-tinted: color-mix(in oklch, var(--surface-solid), var(--color-accent) 10%);
  --glass-alpha:0.62; --glass-blur:12px;
  --color-surface: color-mix(in oklch, var(--surface-tinted) calc(var(--glass-alpha)*100%), transparent);
  --color-line: oklch(0.42 0.014 264);
  --color-ink: oklch(0.95 0.006 90);
  --color-muted: oklch(0.72 0.015 264);
  --color-accent-ink: oklch(0.16 0.012 264);
  --color-ok: oklch(0.72 0.15 155);
  --color-warn: oklch(0.78 0.14 80);
  --color-bad: oklch(0.68 0.18 20);
  --color-pre: oklch(0.70 0.16 300);
  --radius:0.7rem;
  --glass-shadow:0 1px inset color-mix(in oklch,var(--color-ink) 8%,transparent),0 10px 30px -18px rgba(0,0,0,.7);
}
html.light{
  --color-bg: oklch(0.97 0.006 264); --surface-solid: oklch(1 0 0);
  --color-line: oklch(0.84 0.01 264); --color-ink: oklch(0.26 0.012 264);
  --color-muted: oklch(0.48 0.018 264); --color-accent: oklch(0.5 0.13 250);
  --color-accent-ink: oklch(0.985 0.004 90); --color-ok: oklch(0.58 0.15 155);
  --color-warn: oklch(0.62 0.14 75); --color-bad: oklch(0.56 0.2 22); --color-pre: oklch(0.52 0.17 300);
  --glass-shadow:0 1px inset rgba(255,255,255,.7),0 10px 26px -20px rgba(0,0,0,.35);
}
/* The accent palette — instance-dashboard's ten hues, with a darker value per hue in the light theme
   so each one stays legible on a near-white surface. Indigo is the default (the :root value). */
html[data-accent="blue"]{--color-accent:oklch(0.72 0.15 245)}
html[data-accent="teal"]{--color-accent:oklch(0.78 0.12 195)}
html[data-accent="green"]{--color-accent:oklch(0.78 0.15 150)}
html[data-accent="amber"]{--color-accent:oklch(0.80 0.15 74)}
html[data-accent="orange"]{--color-accent:oklch(0.74 0.16 46)}
html[data-accent="rose"]{--color-accent:oklch(0.70 0.17 15)}
html[data-accent="violet"]{--color-accent:oklch(0.70 0.16 300)}
html[data-accent="purple"]{--color-accent:oklch(0.68 0.17 322)}
html[data-accent="pink"]{--color-accent:oklch(0.73 0.16 350)}
html.light[data-accent="blue"]{--color-accent:oklch(0.50 0.16 245)}
html.light[data-accent="teal"]{--color-accent:oklch(0.50 0.10 200)}
html.light[data-accent="green"]{--color-accent:oklch(0.50 0.13 150)}
html.light[data-accent="amber"]{--color-accent:oklch(0.52 0.12 68)}
html.light[data-accent="orange"]{--color-accent:oklch(0.54 0.16 46)}
html.light[data-accent="rose"]{--color-accent:oklch(0.52 0.19 18)}
html.light[data-accent="violet"]{--color-accent:oklch(0.52 0.17 300)}
html.light[data-accent="purple"]{--color-accent:oklch(0.50 0.17 322)}
html.light[data-accent="pink"]{--color-accent:oklch(0.53 0.17 350)}
html{color-scheme:dark} html.light{color-scheme:light}
*{box-sizing:border-box}
html,body{height:100%}
/* ⛔ THE BACKGROUND LIVES ON A FIXED PSEUDO-ELEMENT, NOT ON body WITH background-attachment:fixed.
   Lifted verbatim from instance-dashboard, including its reason: Chrome composites a
   fixed-attachment background separately, so .glass backdrop-filter has nothing to sample and the
   blur looks dead. A position:fixed painted layer behind all content is viewport-pinned the same
   way but IS a real layer the filter can blur. */
body::before{content:"";position:fixed;inset:0;z-index:-1;pointer-events:none;
  background-image:
    radial-gradient(900px 600px at 12% -10%, oklch(0.6 0.16 264 / 0.35), transparent 65%),
    radial-gradient(800px 600px at 100% 110%, oklch(0.55 0.15 300 / 0.25), transparent 65%),
    linear-gradient(oklch(0.16 0.012 264 / 0.40), oklch(0.16 0.012 264 / 0.55)),
    url("/assets/bg-dark.jpg");
  background-size:auto,auto,cover,cover;background-position:center;background-repeat:no-repeat}
html.light body::before{
  background-image:
    radial-gradient(900px 600px at 12% -10%, oklch(0.7 0.13 264 / 0.16), transparent 65%),
    radial-gradient(800px 600px at 100% 110%, oklch(0.7 0.1 300 / 0.12), transparent 65%),
    linear-gradient(oklch(0.97 0.006 264 / 0.55), oklch(0.97 0.006 264 / 0.68)),
    url("/assets/bg-light.jpg")}
/* Photo off, ambient gradients kept for depth; panels go nearly opaque so they still read as
   elevated cards over a flat page. */
html.no-bg body::before{
  background-image:
    radial-gradient(900px 600px at 12% -10%, oklch(0.6 0.16 264 / 0.18), transparent 65%),
    radial-gradient(800px 600px at 100% 110%, oklch(0.55 0.15 300 / 0.14), transparent 65%)}
html.no-bg.light body::before{
  background-image:
    radial-gradient(900px 600px at 12% -10%, oklch(0.7 0.13 264 / 0.10), transparent 65%),
    radial-gradient(800px 600px at 100% 110%, oklch(0.7 0.1 300 / 0.08), transparent 65%)}
html.no-bg{--glass-alpha:0.92}
body{margin:0;background:var(--color-bg);color:var(--color-ink);
  font:14px/1.55 ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  /* ⛔ The page itself never scrolls. Each column scrolls independently, so the finding list
     stays in view while the detail pane moves. */
  overflow:hidden}
header{display:flex;align-items:center;gap:11px;padding:13px 20px;border-bottom:1px solid var(--color-line)}
h1{margin:0;font-size:15px;font-weight:700;letter-spacing:.01em}
.sub{font-size:12px;color:var(--color-muted)}
.pill{display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;white-space:nowrap}
.ok{background:color-mix(in oklch,var(--color-ok) 22%,transparent);color:var(--color-ok)}
.bad{background:color-mix(in oklch,var(--color-bad) 22%,transparent);color:var(--color-bad)}
.warn{background:color-mix(in oklch,var(--color-warn) 20%,transparent);color:var(--color-warn)}
.pre{background:color-mix(in oklch,var(--color-pre) 22%,transparent);color:var(--color-pre)}
.mut{background:color-mix(in oklch,var(--color-muted) 18%,transparent);color:var(--color-muted)}
.acc{background:color-mix(in oklch,var(--color-accent) 22%,transparent);color:var(--color-accent)}
.prefs{margin-left:auto;display:flex;align-items:center;gap:7px;position:relative}
/* The appearance menu — instance-dashboard's SettingsMenu: theme, background, accent,
   transparency, blur, colours, reset. */
.themebtn{display:flex;align-items:center;gap:6px;padding:4px 9px;font-size:12px;color:var(--color-muted)}
.themebtn[aria-expanded="true"]{border-color:var(--color-accent);color:var(--color-ink)}
.themedot{display:inline-block;width:12px;height:12px;border-radius:50%;background:var(--color-accent);
  box-shadow:0 0 0 1px var(--color-line)}
.prefs .menu{position:absolute;right:0;top:calc(100% + 8px);z-index:40;width:14rem;padding:12px;font-size:13px;
  box-shadow:var(--glass-shadow),0 20px 40px -18px rgba(0,0,0,.6)}
.menu .mh{margin:0 0 5px;font-size:10.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--color-muted);
  display:flex;justify-content:space-between}
.seg{display:flex;gap:4px;padding:2px;margin-bottom:12px;border:1px solid var(--color-line);border-radius:calc(var(--radius) - .3rem)}
.seg button{flex:1;padding:4px 8px;font-size:12px;border:0;color:var(--color-muted)}
.seg button[aria-pressed="true"]{background:color-mix(in oklch,var(--color-accent) 20%,transparent);color:var(--color-ink)}
.swatches{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:12px}
.swatches button{width:24px;height:24px;margin:0 auto;padding:0;border-radius:50%;border:0;
  box-shadow:0 0 0 1px var(--color-line)}
.swatches button[aria-pressed="true"]{box-shadow:0 0 0 2px var(--color-surface),0 0 0 4px var(--color-ink)}
.range{width:100%;accent-color:var(--color-accent);height:1.1rem;cursor:pointer;background:transparent;padding:0;border:0}
.rl{display:flex;justify-content:space-between;font-size:10px;color:var(--color-muted);margin:0 0 10px}
.crow{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:12px;color:var(--color-muted);margin:0 0 6px}
.crow input{width:40px;height:24px;padding:0;cursor:pointer;background:transparent;border-radius:6px}
.menu .reset{width:100%;margin-top:6px;font-size:12px;padding:5px}
.layout{display:grid;grid-template-columns:minmax(330px,390px) 1fr;gap:16px;padding:16px 20px;
  height:calc(100vh - 53px)}
/* ⚠️ Narrow screens: one column, and the PAGE scrolls instead of each column. With the desktop rules
   kept, the fixed-height grid squeezed every card (they are overflow:hidden flex items, so they
   shrink to nothing) and the header ran off the right edge at phone width. */
@media(max-width:900px){
  body{overflow:auto;height:auto}
  header{flex-wrap:wrap;row-gap:8px}
  .layout{grid-template-columns:1fr;height:auto;overflow:visible}
  .col,.col.fixed{overflow:visible;padding-right:0}
  .glass{flex-shrink:0}
  #queue{flex:none!important}
  #rows{overflow:visible!important;flex:none!important}
}
@media(max-width:640px){
  .sub{display:none}
  .layout{padding:12px}
  .foot{gap:8px}
  .foot .note{margin-left:0!important;flex-basis:100%}
  /* A finding's title gets its own line under the pills, instead of one word per line beside them. */
  .head{flex-wrap:wrap;row-gap:6px}
  .ttl{order:3;flex-basis:100%}
  .loc{margin-left:auto}
}
.col{min-height:0;display:flex;flex-direction:column;gap:14px;overflow-y:auto;padding-right:4px}
/* ⚠️ Cards never shrink: they are overflow:hidden flex items, so on a short window they were
   squeezed until their own controls were cut off. The column scrolls instead. */
.col>.glass{flex-shrink:0}
/* The queue column does not scroll; the findings list inside it does, so the filter bar and the
   action footer stay put while the list moves. */
.col.fixed{overflow:hidden;padding-right:0}
.glass{background:var(--color-surface);
  border:1px solid color-mix(in oklch,var(--color-accent) 18%,var(--color-line));
  border-radius:var(--radius);box-shadow:var(--glass-shadow);
  -webkit-backdrop-filter:blur(var(--glass-blur)) saturate(1.4);backdrop-filter:blur(var(--glass-blur)) saturate(1.4);
  padding:15px 16px;position:relative;overflow:hidden;
  transition:transform .3s cubic-bezier(.22,1,.36,1),border-color .22s ease-out}
/* Hover and sheen — instance-dashboard's, except the 2px lift: a panel that moves out from under a
   cursor resting on its edge un-hovers and drops back, which is the flicker fixed on buttons above. */
.glass:hover{border-color:color-mix(in oklch,var(--color-accent) 60%,var(--color-line));
  box-shadow:var(--glass-shadow),0 18px 50px -24px rgba(0,0,0,.6),0 0 0 1px color-mix(in oklch,var(--color-accent) 34%,transparent)}
.glass::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;
  background:linear-gradient(115deg,transparent 35%,color-mix(in oklch,var(--color-ink) 7%,transparent) 50%,transparent 65%);
  transform:translateX(-120%);transition:transform 1s cubic-bezier(.22,1,.36,1)}
.glass:hover::after{transform:translateX(120%)}
@media (prefers-reduced-motion:reduce){*{transition:none!important}.glass::after{display:none}}
:focus-visible{outline:none;box-shadow:0 0 0 3px color-mix(in oklch,var(--color-accent) 45%,transparent)}
::selection{background:color-mix(in oklch,var(--color-accent) 40%,transparent)}
h2{margin:0 0 11px;font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--color-muted)}
label{display:block;font-size:11px;color:var(--color-muted);margin:10px 0 4px}
input,select{width:100%;padding:8px 10px;background:color-mix(in oklch,var(--color-bg) 70%,transparent);
  border:1px solid var(--color-line);border-radius:calc(var(--radius) - .25rem);color:var(--color-ink);
  font:13px ui-monospace,SFMono-Regular,Menlo,monospace}
input:focus,select:focus{outline:none;border-color:color-mix(in oklch,var(--color-accent) 60%,var(--color-line))}
button{font:inherit;cursor:pointer;border-radius:calc(var(--radius) - .25rem);
  border:1px solid var(--color-line);background:transparent;color:var(--color-ink);
  padding:7px 13px;font-size:13px;font-weight:500;transition:all .15s ease-out}
/* ⚠️ No transform on hover. A 1px lift moves the button out from under a cursor resting on its
   bottom edge, which un-hovers it, which drops it back under the cursor: the button flickers and
   is hard to click. Colour alone carries the hover. */
button:hover{border-color:color-mix(in oklch,var(--color-accent) 60%,var(--color-line));
  background:color-mix(in oklch,var(--color-accent) 10%,transparent)}
button:active{transform:translateY(0) scale(.97)}
button:disabled{opacity:.45;cursor:not-allowed;transform:none}
button.primary{width:100%;margin-top:14px;padding:10px;background:var(--color-accent);
  color:var(--color-accent-ink);border-color:var(--color-accent);font-weight:600}
button.okb{border-color:var(--color-ok);color:var(--color-ok)}
button.okb:hover{background:color-mix(in oklch,var(--color-ok) 15%,transparent)}
button.badb{border-color:var(--color-bad);color:var(--color-bad)}
button.badb:hover{background:color-mix(in oklch,var(--color-bad) 15%,transparent)}
.dim{display:flex;align-items:center;justify-content:space-between;padding:6px 0;font-size:13px;
  border-bottom:1px solid color-mix(in oklch,var(--color-line) 50%,transparent)}
.dim:last-child{border-bottom:0}
.tog{position:relative;width:34px;height:19px;border-radius:999px;border:1px solid var(--color-line);
  background:color-mix(in oklch,var(--color-bg) 60%,transparent);cursor:pointer;padding:0;flex:0 0 auto}
.tog[aria-checked="true"]{background:var(--color-accent);border-color:var(--color-accent)}
.tog::after{content:"";position:absolute;top:2px;left:2px;width:13px;height:13px;border-radius:50%;
  background:var(--color-ink);transition:transform .16s ease-out}
.tog[aria-checked="true"]::after{transform:translateX(15px);background:var(--color-accent-ink)}
.bar{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:11px}
.fil{padding:3px 10px;border-radius:999px;font-size:11.5px;font-weight:600;cursor:pointer;
  border:1px solid var(--color-line);background:transparent;color:var(--color-muted)}
.fil[aria-pressed="true"]{border-color:var(--color-accent);color:var(--color-accent);
  background:color-mix(in oklch,var(--color-accent) 14%,transparent)}
.rows{list-style:none;margin:0;padding:0 0 4px}
.row{border:1px solid var(--color-line);border-radius:calc(var(--radius) - .2rem);margin-bottom:8px;
  transition:border-color .16s ease-out}
.row:hover{border-color:color-mix(in oklch,var(--color-accent) 50%,var(--color-line))}
.row[data-sel="1"]{border-color:var(--color-accent);
  box-shadow:0 0 0 1px color-mix(in oklch,var(--color-accent) 40%,transparent)}
.row[data-state="accepted"]{border-left:3px solid var(--color-ok)}
.row[data-state="dismissed"]{border-left:3px solid var(--color-bad);opacity:.55}
.head{display:flex;align-items:center;gap:9px;padding:9px 11px;cursor:pointer}
.ttl{flex:1;font-size:13px;line-height:1.4}
.loc{font:11px ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--color-muted);white-space:nowrap}
.body{padding:2px 13px 13px;border-top:1px solid color-mix(in oklch,var(--color-line) 55%,transparent)}
.body h3{margin:12px 0 5px;font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--color-muted);font-weight:700}
.body p{margin:0;font-size:13px;line-height:1.6}
pre{margin:0;padding:10px 12px;background:color-mix(in oklch,var(--color-ink) 6%,transparent);
  border:1px solid var(--color-line);border-radius:calc(var(--radius) - .25rem);overflow-x:auto;
  font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap}
.checks{display:grid;grid-template-columns:1fr 1fr;gap:3px 16px;font-size:12px;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.pass{color:var(--color-ok)} .failc{color:var(--color-bad)}
.acts{display:flex;gap:8px;margin-top:13px}
/* ⚠️ The footer is sticky, so it must be OPAQUE and the list must reserve room beneath it.
   A translucent sticky bar rendered the last two findings unreadable underneath it. */
.foot{display:flex;align-items:center;gap:10px;padding:11px 14px;margin:0 -16px -15px;
  border-top:1px solid var(--color-line);
  background:color-mix(in oklch,var(--surface-solid) 88%,transparent);
  -webkit-backdrop-filter:blur(14px) saturate(1.4);backdrop-filter:blur(14px) saturate(1.4);
  flex-wrap:wrap;z-index:2;flex:0 0 auto}
.note{font-size:11.5px;color:var(--color-muted);line-height:1.5}
.kbd{display:inline-block;padding:1px 5px;border:1px solid var(--color-line);border-radius:4px;
  font:11px ui-monospace,monospace;color:var(--color-muted)}
.banner{display:flex;gap:9px;align-items:flex-start;padding:10px 13px;border-radius:calc(var(--radius) - .2rem);
  font-size:12px;line-height:1.55;border:1px solid color-mix(in oklch,var(--color-warn) 40%,var(--color-line));
  background:color-mix(in oklch,var(--color-warn) 9%,transparent);color:var(--color-warn);margin-bottom:14px}
.dot{flex:0 0 auto;width:8px;height:8px;border-radius:50%;background:var(--color-warn);margin-top:5px}
::-webkit-scrollbar{width:9px;height:9px}
::-webkit-scrollbar-thumb{background:var(--color-line);border-radius:9px}
::-webkit-scrollbar-thumb:hover{background:var(--color-accent)}
</style>
</head>
<body>
<header>
  <h1>sibyl</h1>
  <span class="sub">pull-request review &middot; triage before anything is posted</span>
  <span class="pill bad" title="never expose this">127.0.0.1 only</span>
  <span class="pill warn" id="lockpill">3 locks engaged</span>
  <div class="prefs" id="prefs">
    <button type="button" class="themebtn" id="themebtn" aria-label="Appearance settings" aria-expanded="false">
      <span class="themedot"></span>theme</button>
    <div class="menu glass" id="thememenu" hidden>
      <div class="mh">Appearance</div>
      <div class="seg"><button type="button" data-theme="dark">Dark</button><button type="button" data-theme="light">Light</button></div>
      <div class="mh">Background</div>
      <div class="seg"><button type="button" data-bgset="on">Image</button><button type="button" data-bgset="off">Solid</button></div>
      <div class="mh">Accent</div>
      <div class="swatches" id="swatches"></div>
      <div class="mh"><span>Transparency</span><span id="glassval"></span></div>
      <input type="range" class="range" id="glass" aria-label="Panel transparency" min="0.35" max="1" step="0.01"/>
      <div class="rl"><span>Clear</span><span>Solid</span></div>
      <div class="mh"><span>Blur</span><span id="blurval"></span></div>
      <input type="range" class="range" id="blur" aria-label="Panel blur" min="0" max="24" step="1"/>
      <div class="rl"><span>Crisp</span><span>Frosted</span></div>
      <div class="mh">Colors</div>
      <label class="crow"><span>Text</span><input type="color" id="c-ink" aria-label="Text color"/></label>
      <label class="crow"><span>Glass panel</span><input type="color" id="c-surface" aria-label="Glass panel color"/></label>
      <label class="crow"><span>Background</span><input type="color" id="c-bg" aria-label="Background color"/></label>
      <button type="button" class="reset" id="themereset">Reset to defaults</button>
    </div>
  </div>
</header>

<div class="layout">
  <!-- LEFT: the run surface -->
  <div class="col">
    <div class="glass">
      <h2>Review</h2>
      <label for="repo">Repository</label>
      <input id="repo" value="acme/invoicing" spellcheck="false"/>
      <label for="pr">Pull request</label>
      <input id="pr" value="42" spellcheck="false"/>
      <label for="backend">Backend</label>
      <select id="backend">
        <option value="claude_cli">claude CLI — subscription, no token cost</option>
        <option value="anthropic_api">Anthropic API — metered, isolated limits</option>
      </select>
      <label for="ref">Reviewing</label>
      <select id="ref">
        <option>refs/pull/42/merge — the commit that ships</option>
        <option>refs/pull/42/head — the branch as pushed</option>
      </select>
      <button class="primary" id="run">Review</button>
      <p class="note" style="margin-top:10px">Nothing runs on a schedule and nothing posts on its own.
      This button is the only way a review starts.</p>
    </div>

    <div class="glass">
      <h2>Dimensions</h2>
      <div id="dims"></div>
    </div>

    <div class="glass">
      <h2>Credentials</h2>
      <div id="creds"></div>
      <p class="note" style="margin-top:9px">Set here, never read back. Presence and the last four
      characters only — a leaked token is not recoverable, so this page must not be able to show one.</p>
    </div>
  </div>

  <!-- RIGHT: the triage queue -->
  <div class="col fixed">
    <div class="glass" id="queue" style="padding-bottom:0;flex:1;display:flex;flex-direction:column;min-height:0">
      <!-- ⛔ THIS SLOT USED TO CARRY A HARD-CODED DISCLAIMER SAYING THE QUEUE WAS FIXTURES. It now
           carries the real count, and when the store is empty it carries the reason it is empty —
           rendered from /api/state, never written here, so it cannot go stale the way the sentence
           it replaced did. -->
      <div id="qbanner"></div>

      <h2 style="display:flex;align-items:center;gap:9px">Triage queue
        <span class="pill acc" id="count">0</span>
        <span style="margin-left:auto;font-size:11px;text-transform:none;letter-spacing:0">
          <span class="kbd">j</span> <span class="kbd">k</span> move ·
          <span class="kbd">a</span> accept · <span class="kbd">d</span> dismiss ·
          <span class="kbd">enter</span> expand
        </span>
      </h2>

      <div class="bar" id="filters"></div>
      <ul class="rows" id="rows" style="padding-bottom:8px;flex:1;overflow-y:auto;min-height:0"></ul>

      <!-- ⚠️ The labels say what the buttons do against the DURABLE RECORD. "Accept all LOW with a
           patch" and "Dismiss all pre-existing" were written against fixtures: the stored record
           carries neither a suggested patch nor the introduced/pre-existing class, so those two
           buttons could only ever have acted on a field that is not there. -->
      <div class="foot">
        <button id="expandall">Expand all shown</button>
        <button id="acceptlow">Accept all LOW shown</button>
        <button id="dismissshown">Dismiss all shown…</button>
        <span class="note" style="margin-left:auto" id="summary"></span>
        <button class="okb" id="post" disabled>Post accepted…</button>
      </div>
    </div>
  </div>
</div>

<script>
const $=id=>document.getElementById(id);
// ⛔ EVERY ONE OF THESE COMES FROM /api/state, WHICH READS THE STORE ON DISK. There is no seeded
// array and no fallback: an empty queue renders as an empty queue and says why it is empty.
let Q={counts:{todo:0,accepted:0,dismissed:0,total:0},empty:true,note:'',findings:[]};
let F=[], REASONS=[], LOCKS={engaged:3,total:3}, problem='', picking='';
let sel=0, open=new Set(), filters={sev:new Set(), dim:new Set(), hideSettled:true};

// ── The appearance menu (instance-dashboard's SettingsMenu, same controls and keys) ──
const ACCENTS=[['indigo','Indigo','oklch(0.72 0.13 250)'],['blue','Blue','oklch(0.72 0.15 245)'],
  ['teal','Teal','oklch(0.78 0.12 195)'],['green','Green','oklch(0.78 0.15 150)'],['amber','Amber','oklch(0.80 0.15 74)'],
  ['orange','Orange','oklch(0.74 0.16 46)'],['rose','Rose','oklch(0.70 0.17 15)'],['violet','Violet','oklch(0.70 0.16 300)'],
  ['purple','Purple','oklch(0.68 0.17 322)'],['pink','Pink','oklch(0.73 0.16 350)']];
const GLASS_DEFAULT=0.62, BLUR_DEFAULT=12;
// Picker starting swatches only — nothing is applied until somebody picks.
const COLOR_DEFAULTS={ink:['#f1f0ec','#3b3f46'],surface:['#3a3d42','#ffffff'],bg:['#14161c','#f6f7f9']};
const COLOR_KEYS={ink:['sibyl-ink','--color-ink'],surface:['sibyl-surface','--surface-solid'],bg:['sibyl-bgcolor','--color-bg']};
const H=document.documentElement;
const store=(k,v)=>{try{v===null?localStorage.removeItem(k):localStorage.setItem(k,v)}catch(e){}};
const read=k=>{try{return localStorage.getItem(k)||''}catch(e){return ''}};
$('swatches').innerHTML=ACCENTS.map(a=>'<button type="button" data-accent="'+a[0]+'" title="'+a[1]+'" aria-label="'+a[1]
  +'" style="background:'+a[2]+'"></button>').join('');
function syncMenu(){
  const light=H.classList.contains('light');
  document.querySelectorAll('[data-theme]').forEach(b=>b.setAttribute('aria-pressed',(b.dataset.theme==='light')===light));
  const bgOn=!H.classList.contains('no-bg');
  document.querySelectorAll('[data-bgset]').forEach(b=>b.setAttribute('aria-pressed',(b.dataset.bgset==='on')===bgOn));
  const acc=H.getAttribute('data-accent')||'indigo';
  document.querySelectorAll('#swatches [data-accent]').forEach(b=>b.setAttribute('aria-pressed',b.dataset.accent===acc));
  const cs=getComputedStyle(H);
  const g=parseFloat(cs.getPropertyValue('--glass-alpha')), bl=parseFloat(cs.getPropertyValue('--glass-blur'));
  $('glass').value=String(Number.isFinite(g)?Math.min(1,Math.max(0.35,g)):GLASS_DEFAULT);
  $('glassval').textContent=Math.round(parseFloat($('glass').value)*100)+'%';
  $('blur').value=String(Number.isFinite(bl)?Math.min(24,Math.max(0,bl)):BLUR_DEFAULT);
  $('blurval').textContent=Math.round(parseFloat($('blur').value))+'px';
  for(const k of ['ink','surface','bg']) $('c-'+k).value=read(COLOR_KEYS[k][0])||COLOR_DEFAULTS[k][light?1:0];
}
$('themebtn').onclick=e=>{e.stopPropagation();const open=$('thememenu').hidden;$('thememenu').hidden=!open;
  $('themebtn').setAttribute('aria-expanded',open);if(open)syncMenu();};
document.addEventListener('mousedown',e=>{if(!$('thememenu').hidden&&!$('prefs').contains(e.target)){
  $('thememenu').hidden=true;$('themebtn').setAttribute('aria-expanded','false');}});
document.addEventListener('keydown',e=>{if(e.key==='Escape'&&!$('thememenu').hidden){
  $('thememenu').hidden=true;$('themebtn').setAttribute('aria-expanded','false');}});
document.querySelectorAll('[data-theme]').forEach(b=>b.onclick=()=>{
  H.classList.toggle('light',b.dataset.theme==='light');store('sibyl-theme',b.dataset.theme);syncMenu();});
document.querySelectorAll('[data-bgset]').forEach(b=>b.onclick=()=>{
  H.classList.toggle('no-bg',b.dataset.bgset==='off');store('sibyl-bg',b.dataset.bgset);syncMenu();});
document.querySelectorAll('#swatches [data-accent]').forEach(b=>b.onclick=()=>{
  const id=b.dataset.accent; if(id==='indigo')H.removeAttribute('data-accent'); else H.setAttribute('data-accent',id);
  store('sibyl-accent',id);syncMenu();});
// An inline value wins over html.no-bg's --glass-alpha, so an explicit choice sticks in Solid mode too.
$('glass').oninput=()=>{const v=Math.min(1,Math.max(0.35,parseFloat($('glass').value)));
  H.style.setProperty('--glass-alpha',String(v));store('sibyl-glass',String(v));syncMenu();};
$('blur').oninput=()=>{const v=Math.min(24,Math.max(0,Math.round(parseFloat($('blur').value))));
  H.style.setProperty('--glass-blur',v+'px');store('sibyl-blur',String(v));syncMenu();};
for(const k of ['ink','surface','bg']) $('c-'+k).oninput=()=>{const hex=$('c-'+k).value;
  H.style.setProperty(COLOR_KEYS[k][1],hex);store(COLOR_KEYS[k][0],hex);};
// Reset to the shipped defaults: dark · indigo · image background · default translucency and blur ·
// theme colours. Clears every persisted key and updates the page live.
$('themereset').onclick=()=>{
  H.classList.remove('light','no-bg');H.removeAttribute('data-accent');
  for(const v of ['--glass-alpha','--glass-blur','--color-ink','--surface-solid','--color-bg'])H.style.removeProperty(v);
  for(const k of ['sibyl-theme','sibyl-bg','sibyl-accent','sibyl-glass','sibyl-blur','sibyl-ink','sibyl-surface','sibyl-bgcolor'])store(k,null);
  syncMenu();};

const esc=s=>String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
const st=f=>f.state&&f.state.kind?f.state.kind:'todo';
const sevPill=f=>'<span class="pill '+(f.severity==='high'?'bad':f.severity==='medium'?'warn':'mut')+'">'
  +esc(f.severity)+'</span>';
const words=s=>esc(String(s).replace(/_/g,' '));

function visible(){
  return F.filter(f=>{
    // Mirrors \`sibyl triage\`, which lists what is still to decide and takes --all for the rest.
    if(filters.hideSettled && st(f)!=='todo') return false;
    if(filters.sev.size && !filters.sev.has(f.severity)) return false;
    if(filters.dim.size && !filters.dim.has(f.dimensionId)) return false;
    return true;
  });
}

function banner(){
  // ⛔ A refused decision is shown as refused, above the queue, in the degraded tone. The alternative
  // is a row that sits on screen looking as though it was recorded when the store rejected it.
  if(problem) return '<div class="banner"><span class="dot"></span><span>'+esc(problem)+'</span></div>';
  // The empty queue is the degraded state: it means no review has run, which must never be mistaken
  // for a review that found nothing.
  if(Q.empty) return '<div class="banner"><span class="dot"></span><span>'+esc(Q.note)+'</span></div>';
  return '<p class="note" style="margin:0 0 12px">'+esc(Q.note)+'</p>';
}

function render(){
  const v=visible();
  $('qbanner').innerHTML=banner();
  // ⛔ The waiting count carries its denominator. "3 waiting" over 3 findings and over 40 are
  // different situations and only one of them is nearly done.
  $('count').textContent=Q.counts.todo+' of '+Q.counts.total+' waiting';
  if(sel>=v.length) sel=Math.max(0,v.length-1);
  $('rows').innerHTML=v.map((f,i)=>{
    const isOpen=open.has(f.fingerprint);
    return '<li class="row" data-fp="'+esc(f.fingerprint)+'" data-sel="'+(i===sel?1:0)+'" data-state="'+st(f)+'">'
      +'<div class="head" data-fp="'+esc(f.fingerprint)+'">'
        +sevPill(f)
        +'<span class="pill mut">'+words(f.dimensionId)+'</span>'
        +'<span class="ttl">'+esc(f.title)+'</span>'
        +(f.timesSeen>1?'<span class="pill mut" title="reviews that raised it">seen '+esc(f.timesSeen)+'</span>':'')
        +(st(f)==='accepted'?'<span class="pill ok">accepted</span>':'')
        +(st(f)==='dismissed'?'<span class="pill bad">'+words(f.state.reason)+'</span>':'')
        +'<span class="loc">'+esc(String(f.lastAnchor.path).split('/').pop())+':'+esc(f.lastAnchor.line)+'</span>'
      +'</div>'
      +(isOpen?detail(f):'')
    +'</li>';
  }).join('') || '<p class="note">'+(Q.empty?'':'Nothing matches these filters.')+'</p>';

  const hidden=F.length-v.length;
  $('summary').textContent=Q.counts.accepted+' accepted · '+Q.counts.dismissed+' dismissed · '
    +Q.counts.todo+' to triage'
    // ⛔ What the filter hid is stated, not omitted — the same rule \`sibyl triage\` follows. A queue
    // that quietly shows 3 of 19 reads as a review that found three things.
    +(hidden>0?' · '+hidden+' hidden by filters':'');
  $('post').disabled=Q.counts.accepted===0;
  $('post').textContent=Q.counts.accepted?('Post '+Q.counts.accepted+' accepted…'):'Post accepted…';

  document.querySelectorAll('.head').forEach(h=>h.onclick=()=>{
    const fp=h.dataset.fp; open.has(fp)?open.delete(fp):open.add(fp);
    sel=visible().findIndex(x=>x.fingerprint===fp); render();
  });
  document.querySelectorAll('[data-act]').forEach(b=>b.onclick=e=>{
    e.stopPropagation(); onAct(b.dataset.fp, b.dataset.act);
  });
}

/**
 * ⚠️ WHAT THE DURABLE RECORD ACTUALLY HOLDS, AND NOTHING ELSE. The quoted code, the failure
 * scenario, the suggested fix and the component checks belong to the review that produced the
 * finding and are not written to the triage store — so this pane shows identity, location, history
 * and the decision. Rendering the component grid against fields that are not there would print six
 * failed checks under every real finding, which is a worse lie than omitting them.
 */
function detail(f){
  const s=f.state||{kind:'todo'};
  const decided=s.kind==='accepted'
    ? 'Accepted by '+esc(s.by)+' at '+esc(s.at)+'.'
    : s.kind==='dismissed'
      ? 'Dismissed by '+esc(s.by)+' at '+esc(s.at)+' as <strong>'+words(s.reason)+'</strong>.'
        +(s.note?' '+esc(s.note):'')
      : 'No decision yet. Nothing is posted from this queue until one is made.';
  return '<div class="body">'
    +'<h3>Last seen at</h3><pre>'+esc(f.lastAnchor.path)+':'+esc(f.lastAnchor.line)
      +' ('+esc(f.lastAnchor.side)+')</pre>'
    +'<p class="note" style="margin-top:6px">Advisory only. Identity is the fingerprint below, never the'
      +' path or the line — both move under a rebase and the defect does not.</p>'
    +'<h3>Fingerprint</h3><pre>'+esc(f.fingerprint)+'</pre>'
    +'<h3>Decision</h3><p>'+decided+'</p>'
    +'<h3>History</h3><p class="note">rule '+esc(f.ruleId)+' · first seen '+esc(f.firstSeenSha).slice(0,12)
      +' at '+esc(f.firstSeenAt)+' · last seen '+esc(f.lastSeenSha).slice(0,12)
      +' · raised by '+esc(f.timesSeen)+' review'+(f.timesSeen===1?'':'s')
      +' · updated '+esc(f.updatedAt)+'</p>'
    +'<div class="acts">'+acts(f)+'</div></div>';
}

function acts(f){
  const fp=esc(f.fingerprint);
  if(picking===f.fingerprint){
    // ⛔ THE VOCABULARY IS THE SERVER'S, AND THERE IS NO PRE-SELECTED REASON. A default would let a
    // person dismiss a finding as "false positive" without ever deciding it was one, and the
    // dismissal record exists to tell a false positive apart from something nobody chose to fix.
    return '<select id="reason" style="flex:1">'
        +'<option value="" selected disabled>choose a reason…</option>'
        +REASONS.map(r=>'<option value="'+esc(r)+'">'+words(r)+'</option>').join('')
      +'</select>'
      +'<input id="dnote" placeholder="note (optional)" style="flex:1"/>'
      +'<button class="badb" data-act="confirm" data-fp="'+fp+'">Dismiss</button>'
      +'<button data-act="cancel" data-fp="'+fp+'">Cancel</button>';
  }
  return '<button class="okb" data-act="accept" data-fp="'+fp+'">Accept</button>'
    +'<button class="badb" data-act="pick" data-fp="'+fp+'">Dismiss…</button>';
}

/**
 * ⛔ THE ONLY PLACE A DECISION IS MADE, AND IT IS MADE ON THE SERVER. The page holds no local copy of
 * a state it is about to set: it sends the decision, and re-renders from the queue that came back.
 * A click the store refuses can therefore never leave the screen saying it was recorded.
 */
async function post(payload){
  problem='';
  let r, j;
  try{
    r=await fetch('/api/triage',{method:'POST',headers:{'content-type':'application/json'},
      body:JSON.stringify(payload)});
    j=await r.json();
  }catch(e){
    problem='The console could not reach the queue: '+e; render(); return false;
  }
  if(!r.ok){
    problem=(j&&j.error?j.error:('the queue refused this with status '+r.status))
      +(j&&j.allowed?' — allowed: '+j.allowed.join(', '):'')
      +(j&&j.matches?' — '+j.matches.join(' '):'');
    render(); return false;
  }
  Q=j.queue; F=Q.findings; render(); return true;
}

function onAct(fp,act){
  if(act==='pick'){ picking=fp; open.add(fp); render(); return; }
  if(act==='cancel'){ picking=''; render(); return; }
  if(act==='accept'){ picking=''; post({fingerprint:fp,action:'accept'}); return; }
  if(act==='confirm'){
    const reason=$('reason').value, note=$('dnote').value;
    if(!reason){ problem='Pick a dismissal reason first — a dismissal with no reason is refused.';
      render(); return; }
    picking='';
    post({fingerprint:fp,action:'dismiss',reason:reason,note:note});
  }
}

document.addEventListener('keydown',e=>{
  if(/input|select|textarea/i.test(e.target.tagName)) return;
  const v=visible(); if(!v.length) return;
  if(e.key==='j'){sel=Math.min(sel+1,v.length-1);render();}
  else if(e.key==='k'){sel=Math.max(sel-1,0);render();}
  else if(e.key==='Enter'){const fp=v[sel].fingerprint;open.has(fp)?open.delete(fp):open.add(fp);render();}
  else if(e.key==='a'){onAct(v[sel].fingerprint,'accept');}
  // ⛔ \`d\` opens the reason picker rather than dismissing. A one-key dismissal would have to invent
  // the reason, and the reason is the whole value of the record.
  else if(e.key==='d'){onAct(v[sel].fingerprint,'pick');}
});

$('expandall').onclick=()=>{visible().forEach(f=>open.add(f.fingerprint));render();};
$('acceptlow').onclick=async()=>{
  for(const f of visible().filter(f=>f.severity==='low'&&st(f)==='todo')){
    if(!await post({fingerprint:f.fingerprint,action:'accept'})) return;
  }
};
$('dismissshown').onclick=async()=>{
  const shown=visible().filter(f=>st(f)==='todo');
  if(!shown.length) return;
  // ⛔ One reason for the batch, chosen before anything is written, and each finding still gets its
  // own dated record on the server. A bulk action that skipped the reason would be the same
  // unanswerable dismissal as a single one.
  const reason=window.prompt('Dismiss '+shown.length+' shown findings as which reason?\\n'
    +REASONS.join(', '));
  if(!reason) return;
  for(const f of shown){
    if(!await post({fingerprint:f.fingerprint,action:'dismiss',reason:reason})) return;
  }
};
$('post').onclick=()=>alert('Posting is not wired to this page. '+LOCKS.engaged+' of '+LOCKS.total
  +' locks are engaged; post from the command line, where the confirmation token is entered.');
$('run').onclick=()=>alert('The console does not start reviews. Run \`sibyl review <pr>\`; its findings'
  +' land in this queue and the page picks them up on reload.');

function buildFilters(){
  const dims=[...new Set(F.map(f=>f.dimensionId))];
  $('filters').innerHTML=
    ['high','medium','low'].map(s=>'<button class="fil" data-sev="'+s+'" aria-pressed="false">'+s+'</button>').join('')
    +dims.map(d=>'<button class="fil" data-dim="'+esc(d)+'" aria-pressed="false">'+words(d)+'</button>').join('')
    +'<button class="fil" id="setfil" aria-pressed="true">hide decided</button>';
  document.querySelectorAll('[data-sev]').forEach(b=>b.onclick=()=>{
    filters.sev.has(b.dataset.sev)?filters.sev.delete(b.dataset.sev):filters.sev.add(b.dataset.sev);
    b.setAttribute('aria-pressed',filters.sev.has(b.dataset.sev));sel=0;render();});
  document.querySelectorAll('[data-dim]').forEach(b=>b.onclick=()=>{
    filters.dim.has(b.dataset.dim)?filters.dim.delete(b.dataset.dim):filters.dim.add(b.dataset.dim);
    b.setAttribute('aria-pressed',filters.dim.has(b.dataset.dim));sel=0;render();});
  $('setfil').onclick=()=>{filters.hideSettled=!filters.hideSettled;
    $('setfil').setAttribute('aria-pressed',filters.hideSettled);sel=0;render();};
}

(async()=>{
  const s=await (await fetch('/api/state')).json();
  Q=s.queue; F=Q.findings; REASONS=s.dismissReasons; LOCKS=s.locks;
  $('dims').innerHTML=s.dimensions.map(d=>
    '<div class="dim"><span>'+esc(d.title)+'</span>'
    +'<button class="tog" role="switch" aria-checked="'+(d.enabled?'true':'false')+'"></button></div>').join('');
  document.querySelectorAll('.tog').forEach(t=>t.onclick=()=>
    t.setAttribute('aria-checked', t.getAttribute('aria-checked')==='true'?'false':'true'));
  // ⛔ Presence and the last four, exactly as the endpoint sends them. This page has no way to ask
  // for a value and the endpoint has no way to answer.
  $('creds').innerHTML=s.credentials.map(c=>
    '<div class="dim"><span style="font:12px ui-monospace,monospace">'+esc(c.name)+'</span>'
    +(c.set?'<span class="pill ok">set · '+esc(c.hint)+'</span>':'<span class="pill mut">not set</span>')
    +'</div>').join('');
  $('lockpill').textContent=s.locks.engaged+' of '+s.locks.total+' locks engaged';
  buildFilters(); render();
})();
</script>
</body>
</html>`
